import { useState } from "react";
import { api } from "../lib/api";
import { formatPeriod, daysBetween } from "../lib/format";
import Modal from "./Modal";

// Set a budget cycle's start and end dates. Defaults follow the last-Friday
// payday rule (passed in as start/end); edit here when a month differs.
export default function PaydayModal({
  period,
  start,
  end,
  onClose,
  onSaved,
}: {
  period: string;
  start: string;
  end: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(startDate) && /^\d{4}-\d{2}-\d{2}$/.test(endDate) && startDate <= endDate;
  const days = valid ? daysBetween(startDate, endDate) + 1 : 0;
  const weeks = Math.round(days / 7);

  async function save() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate))
      return setError("Pick both dates");
    if (startDate > endDate) return setError("Start must be on or before end");
    setSaving(true);
    try {
      await api.setPayPeriod(period, startDate, endDate);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={`${formatPeriod(period)} cycle`} onClose={onClose}>
      <p className="modal__lede">
        Your budget month runs payday to payday. It defaults to the last Friday of the month — adjust here if this month differs (e.g. a bank holiday).
      </p>
      <div className="form">
        <div className="form__row">
          <label className="field">
            <span className="field__label">Cycle starts (payday)</span>
            <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} autoFocus />
          </label>
          <label className="field">
            <span className="field__label">Cycle ends</span>
            <input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
        </div>
        {valid && (
          <div className="combined-row">
            <span>Cycle length</span>
            <strong><span className={"week-badge week-badge--" + weeks}>{weeks}-week</span> · {days} days</strong>
          </div>
        )}
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions">
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save cycle"}</button>
        </div>
      </div>
    </Modal>
  );
}
